/**
 * Block Kit (contracts blockKit.ts): the rules every platform shares —
 *
 * - the cross-field checks the API applies before storing an app's blocks or opening its modal (`kitProblems`,
 *   `viewProblems`);
 * - Slack mrkdwn → styled spans (`mrkdwnSpans`);
 * - the render model each client draws (`renderKit`: unsafe URLs dropped, block ids filled in, elements resolved to
 *   nodes with their initial values, rich_text flattened to spans);
 * - the values of interactive elements: a client keeps `KitValues` (block id → action id → strings) starting from
 *   `initialKitValues`, checks input blocks with `kitInputErrors` and sends `kitState` (Slack's `state.values`); the
 *   API keeps only real elements and real options with `sanitizeKitState`;
 * - the plain-text fallback for notifications and search when an app sends blocks without `text` (`kitFallbackText`);
 * - option groups (`option_groups`: `KitSelectNode.groups`, flattened into `options`), external selects (choices from
 *   the app: `externalOptionsAnswer` checks its answer) and server-searched pickers (`kitPickerQuery`), and the Home
 *   tab's checks (`homeViewProblems`).
 */
import type { KitBlock, KitButton, KitConfirm, KitElement, KitHomeView, KitImageElement, KitOption, KitOptionGroup, KitRichInline, KitStateValue, KitStateValues, KitText, KitView } from "@bundle/contracts";

/** A run of mrkdwn with its styles. Mentions keep their raw id; the renderer shows the name. */
export type KitSpan = {
  text: string;
  bold?: true;
  italic?: true;
  strike?: true;
  code?: true;
  /** A ``` block (monospace, keeps line breaks). */
  pre?: true;
  link?: string;
  userId?: string;
  channelId?: string;
  /** <!here>, <!channel>, <!everyone>. */
  broadcast?: "here" | "channel" | "everyone";
};

/** "Are you sure?" before an element's choice goes to the app. */
export type KitConfirmNode = { title: string; text: string; confirm: string; deny: string; style: "default" | "primary" | "danger" };
export type KitOptionNode = { value: string; label: string; description?: string };

export type KitButtonNode = {
  kind: "button";
  actionId: string;
  label: string;
  value?: string;
  url?: string;
  style: "default" | "primary" | "danger";
  confirm?: KitConfirmNode;
  accessibilityLabel?: string;
};
export type KitTextInputNode = { kind: "text_input"; type: "plain_text_input"; actionId: string; placeholder?: string; initial: string[]; multiline: boolean; minLength?: number; maxLength?: number; focus: boolean };
/** A labelled group of a select's options (Slack's option_groups). */
export type KitOptionGroupNode = { label: string; options: KitOptionNode[] };
/**
 * Every select: `source` is where the choices come from (the element's options, the app as the person types, the
 * workspace's people, its conversations, its public channels). `initial` holds option values or ids. With
 * option_groups, `groups` has them and `options` is every option in order. External selects list their initial
 * options in `options` (so they have labels) and start searching after `minQueryLength` characters.
 */
export type KitSelectNode = {
  kind: "select";
  type: string;
  actionId: string;
  source: "static" | "external" | "users" | "conversations" | "channels";
  multi: boolean;
  placeholder?: string;
  options: KitOptionNode[];
  groups?: KitOptionGroupNode[];
  minQueryLength?: number;
  initial: string[];
  maxSelected?: number;
  /** conversations: the kinds to list (absent = all). */
  include?: ("im" | "mpim" | "private" | "public")[];
  excludeBots?: true;
  /** conversations: start on the conversation it was opened from. */
  defaultToCurrent?: true;
  confirm?: KitConfirmNode;
  focus: boolean;
};
export type KitDateNode = { kind: "datepicker"; type: "datepicker"; actionId: string; placeholder?: string; initial: string[]; confirm?: KitConfirmNode; focus: boolean };
export type KitTimeNode = { kind: "timepicker"; type: "timepicker"; actionId: string; placeholder?: string; initial: string[]; confirm?: KitConfirmNode; focus: boolean };
export type KitCheckboxesNode = { kind: "checkboxes"; type: "checkboxes"; actionId: string; options: KitOptionNode[]; initial: string[]; confirm?: KitConfirmNode; focus: boolean };
export type KitRadioNode = { kind: "radio_buttons"; type: "radio_buttons"; actionId: string; options: KitOptionNode[]; initial: string[]; confirm?: KitConfirmNode; focus: boolean };
export type KitOverflowNode = { kind: "overflow"; actionId: string; options: KitOptionNode[]; confirm?: KitConfirmNode };

/** An interactive element. Everything but buttons and overflow menus holds a value (`initial`, then `KitValues`). */
export type KitElementNode = KitButtonNode | KitTextInputNode | KitSelectNode | KitDateNode | KitTimeNode | KitCheckboxesNode | KitRadioNode | KitOverflowNode;
/** The elements that hold a value. */
export type KitValueNode = KitTextInputNode | KitSelectNode | KitDateNode | KitTimeNode | KitCheckboxesNode | KitRadioNode;

export type KitImageNode = { kind: "image"; url: string; alt: string };
export type KitContextNode = { kind: "text"; spans: KitSpan[] } | KitImageNode;
/** A piece of a rich_text block. */
export type KitRichNode =
  | { kind: "paragraph"; spans: KitSpan[] }
  | { kind: "list"; ordered: boolean; indent: number; offset: number; items: KitSpan[][] }
  | { kind: "preformatted"; text: string }
  | { kind: "quote"; spans: KitSpan[] };

export type KitNode =
  | { kind: "header"; blockId: string; text: string }
  | { kind: "section"; blockId: string; text?: KitSpan[]; fields: KitSpan[][]; accessory?: KitElementNode | KitImageNode }
  | { kind: "divider"; blockId: string }
  | { kind: "context"; blockId: string; elements: KitContextNode[] }
  | { kind: "image"; blockId: string; url: string; alt: string; title?: string }
  | { kind: "actions"; blockId: string; elements: KitElementNode[] }
  | { kind: "input"; blockId: string; label: string; hint?: string; optional: boolean; dispatch: boolean; element: KitValueNode }
  | { kind: "rich_text"; blockId: string; elements: KitRichNode[] };

/** What a person has entered or picked: block id → action id → values (option values, ids, the text, the day or time). */
export type KitValues = Record<string, Record<string, string[]>>;

/**
 * Labels of options an element doesn't list itself (external selects: what the app answered as the person typed):
 * block id → action id → option value → its text. `state.values` carries that text, like Slack, not the value.
 */
export type KitLabels = Record<string, Record<string, Record<string, string>>>;

/** Why an input block can't be submitted yet. */
export type KitInputError = "required" | "tooShort" | "tooLong" | "tooMany";

/** https URLs only (images load in every client; buttons open in the browser). */
export function isSafeKitUrl(url: string): boolean {
  return /^https:\/\/[^\s/?#]+\.[^\s]*$/i.test(url) && url.length <= 3000;
}

/** An interactive element (any block element but an image). */
type RawElement = Exclude<KitElement, KitImageElement>;

const SELECT_SOURCE: Record<string, { source: KitSelectNode["source"]; multi: boolean }> = {
  static_select: { source: "static", multi: false },
  multi_static_select: { source: "static", multi: true },
  external_select: { source: "external", multi: false },
  multi_external_select: { source: "external", multi: true },
  users_select: { source: "users", multi: false },
  multi_users_select: { source: "users", multi: true },
  conversations_select: { source: "conversations", multi: false },
  multi_conversations_select: { source: "conversations", multi: true },
  channels_select: { source: "channels", multi: false },
  multi_channels_select: { source: "channels", multi: true },
};

/** The element's options (option groups flattened, in order; an external select's initial options). */
const optionsOf = (e: RawElement): readonly KitOption[] => {
  if (e.type === "external_select") return e.initial_option !== undefined ? [e.initial_option] : [];
  if (e.type === "multi_external_select") return e.initial_options ?? [];
  if ("option_groups" in e && e.option_groups !== undefined) return e.option_groups.flatMap((g) => g.options);
  return "options" in e && e.options !== undefined ? e.options : [];
};

/** The element's initial option values / ids / text, as given (unfiltered). */
function rawInitial(e: RawElement): string[] {
  switch (e.type) {
    case "plain_text_input":
      return e.initial_value !== undefined && e.initial_value !== "" ? [e.initial_value] : [];
    case "static_select":
    case "radio_buttons":
      return e.initial_option !== undefined ? [e.initial_option.value] : [];
    case "multi_static_select":
    case "checkboxes":
    case "multi_external_select":
      return (e.initial_options ?? []).map((o) => o.value);
    case "external_select":
      return e.initial_option !== undefined ? [e.initial_option.value] : [];
    case "users_select":
      return e.initial_user !== undefined ? [e.initial_user] : [];
    case "multi_users_select":
      return e.initial_users ?? [];
    case "conversations_select":
      return e.initial_conversation !== undefined ? [e.initial_conversation] : [];
    case "multi_conversations_select":
      return e.initial_conversations ?? [];
    case "channels_select":
      return e.initial_channel !== undefined ? [e.initial_channel] : [];
    case "multi_channels_select":
      return e.initial_channels ?? [];
    case "datepicker":
      return e.initial_date !== undefined ? [e.initial_date] : [];
    case "timepicker":
      return e.initial_time !== undefined ? [e.initial_time] : [];
    default:
      return [];
  }
}

const isValueElement = (e: RawElement) => e.type !== "button" && e.type !== "overflow";

/** Where an element may go (Slack): `undefined` = fine, else why not. */
function placementProblem(e: KitElement, place: "accessory" | "actions" | "input"): string | undefined {
  if (place === "input") return e.type === "button" || e.type === "overflow" || e.type === "image" ? `${e.type} can't go in an input block` : undefined;
  if (e.type === "plain_text_input") return place === "actions" ? "plain_text_input can't go in an actions block" : "plain_text_input can't be an accessory";
  if (e.type === "image" && place === "actions") return "image can't go in an actions block";
  return undefined;
}

/** Every interactive element of the blocks that is where it may go, with its block id ("0", "1", … when absent), in order. */
function elementsOf(blocks: readonly KitBlock[]): { blockId: string; e: RawElement; where: string; input: boolean }[] {
  const out: { blockId: string; e: RawElement; where: string; input: boolean }[] = [];
  const add = (blockId: string, e: KitElement, where: string, place: "accessory" | "actions" | "input") => {
    if (e.type !== "image" && placementProblem(e, place) === undefined) out.push({ blockId, e, where, input: place === "input" });
  };
  blocks.forEach((b, i) => {
    const blockId = b.block_id ?? String(i);
    if (b.type === "section" && b.accessory !== undefined) add(blockId, b.accessory, `blocks[${i}].accessory`, "accessory");
    if (b.type === "actions") b.elements.forEach((e, j) => add(blockId, e, `blocks[${i}].elements[${j}]`, "actions"));
    if (b.type === "input") add(blockId, b.element, `blocks[${i}].element`, "input");
  });
  return out;
}

/**
 * What's wrong with blocks beyond their shape, as `blocks[i]…: …` lines (empty = fine): duplicate block ids,
 * duplicate action ids in a block, a section without text or fields, non-https image or button URLs, duplicate option
 * values, initial options that aren't options, more initial choices than `max_selected_items`, a text input whose
 * `min_length` is over its `max_length` or whose initial value is too long.
 */
export function kitProblems(blocks: readonly KitBlock[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const actionIds = new Map<string, Set<string>>();
  blocks.forEach((b, i) => {
    const at = `blocks[${i}]`;
    if (b.block_id !== undefined) {
      if (seen.has(b.block_id)) out.push(`${at}: duplicate block_id "${b.block_id}"`);
      seen.add(b.block_id);
    }
    const image = (img: { image_url: string }, where: string) => {
      if (!isSafeKitUrl(img.image_url)) out.push(`${where}: image_url must be an https URL`);
    };
    const place = (e: KitElement, where: string, p: "accessory" | "actions" | "input") => {
      const problem = placementProblem(e, p);
      if (problem !== undefined) out.push(`${where}: ${problem}`);
    };
    switch (b.type) {
      case "section":
        if (b.text === undefined && (b.fields === undefined || b.fields.length === 0)) out.push(`${at}: a section needs text or fields`);
        if (b.accessory?.type === "image") image(b.accessory, `${at}.accessory`);
        if (b.accessory !== undefined) place(b.accessory, `${at}.accessory`, "accessory");
        break;
      case "image":
        image(b, at);
        break;
      case "context":
        b.elements.forEach((e, j) => {
          if (e.type === "image") image(e, `${at}.elements[${j}]`);
        });
        break;
      case "actions":
        b.elements.forEach((e, j) => place(e, `${at}.elements[${j}]`, "actions"));
        break;
      case "input":
        place(b.element, `${at}.element`, "input");
        break;
      default:
        break;
    }
  });
  for (const { blockId, e, where } of elementsOf(blocks)) {
    const ids = actionIds.get(blockId) ?? new Set<string>();
    actionIds.set(blockId, ids);
    if (ids.has(e.action_id)) out.push(`${where}: duplicate action_id "${e.action_id}"`);
    ids.add(e.action_id);
    if (e.type === "button") {
      if (e.url !== undefined && !isSafeKitUrl(e.url)) out.push(`${where}: url must be an https URL`);
      continue;
    }
    const opts = optionsOf(e);
    const values = new Set<string>();
    for (const o of opts) {
      if (values.has(o.value)) out.push(`${where}: duplicate option value "${o.value}"`);
      values.add(o.value);
    }
    if (e.type === "static_select" || e.type === "multi_static_select") {
      const hasOptions = e.options !== undefined;
      const hasGroups = e.option_groups !== undefined;
      if (hasOptions === hasGroups) out.push(`${where}: a static select needs options or option_groups (one of them)`);
    }
    if (e.type === "static_select" || e.type === "multi_static_select" || e.type === "checkboxes" || e.type === "radio_buttons") {
      const initial = rawInitial(e);
      if (initial.some((v) => !values.has(v))) out.push(`${where}: initial options must be among the options`);
    }
    if ("max_selected_items" in e && e.max_selected_items !== undefined && rawInitial(e).length > e.max_selected_items) out.push(`${where}: more initial choices than max_selected_items`);
    if (e.type === "plain_text_input") {
      if (e.min_length !== undefined && e.max_length !== undefined && e.min_length > e.max_length) out.push(`${where}: min_length is over max_length`);
      if (e.initial_value !== undefined && e.max_length !== undefined && e.initial_value.length > e.max_length) out.push(`${where}: initial_value is longer than max_length`);
    }
  }
  return out;
}

/**
 * What's wrong with a Home tab beyond its shape (views.publish): `kitProblems` of its blocks. Input blocks are allowed
 * only with `dispatch_action` (the tab has no submit button; Slack sends their changes as block_actions).
 */
export function homeViewProblems(view: KitHomeView): string[] {
  const out = kitProblems(view.blocks);
  view.blocks.forEach((b, i) => {
    if (b.type === "input" && b.dispatch_action !== true) out.push(`blocks[${i}]: input blocks in a Home tab need dispatch_action`);
  });
  return out;
}

/** What's wrong with a modal beyond its shape: `kitProblems` of its blocks, and a submit button when it has inputs. */
export function viewProblems(view: KitView): string[] {
  const out = kitProblems(view.blocks);
  if (view.submit === undefined && view.blocks.some((b) => b.type === "input")) out.push("submit is required when a view has input blocks");
  return out;
}

type Style = Omit<KitSpan, "text">;

const unescape = (s: string) => s.replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&");
const MARKS: Record<string, "bold" | "italic" | "strike"> = { "*": "bold", _: "italic", "~": "strike" };
/** Characters that may sit next to a formatting mark (Slack: start/end, whitespace or punctuation). */
const BOUNDARY = /[\s.,;:!?()[\]{}'"\-/]/;

function sameStyle(a: KitSpan, b: KitSpan): boolean {
  const keys: (keyof Style)[] = ["bold", "italic", "strike", "code", "pre", "link", "userId", "channelId", "broadcast"];
  return keys.every((k) => a[k] === b[k]);
}

function push(out: KitSpan[], span: KitSpan): void {
  if (span.text === "") return;
  const last = out.at(-1);
  if (last !== undefined && sameStyle(last, span) && span.userId === undefined && span.channelId === undefined && span.broadcast === undefined) {
    last.text += span.text;
    return;
  }
  out.push(span);
}

function entity(body: string, label: string | undefined, style: Style): KitSpan {
  if (body.startsWith("@")) return { ...style, text: label ? `@${label}` : body, userId: body.slice(1) };
  if (body.startsWith("#")) return { ...style, text: label ? `#${label}` : body, channelId: body.slice(1) };
  if (body.startsWith("!")) {
    const word = body.slice(1).split("^")[0]!;
    if (word === "here" || word === "channel" || word === "everyone") return { ...style, text: `@${word}`, broadcast: word };
    return { ...style, text: label ?? body };
  }
  const url = unescape(body);
  if (/^(https?:\/\/|mailto:)/i.test(url)) return { ...style, text: label !== undefined && label !== "" ? unescape(label) : url, link: url };
  return { ...style, text: unescape(label ?? body) };
}

function parse(src: string, style: Style, out: KitSpan[]): void {
  let plain = "";
  const flush = () => {
    push(out, { ...style, text: unescape(plain) });
    plain = "";
  };
  let i = 0;
  while (i < src.length) {
    const ch = src[i]!;
    if (src.startsWith("```", i)) {
      const end = src.indexOf("```", i + 3);
      if (end > i + 3) {
        flush();
        push(out, { text: unescape(src.slice(i + 3, end).replace(/^\n/, "").replace(/\n$/, "")), pre: true });
        i = end + 3;
        continue;
      }
    }
    if (ch === "`") {
      const end = src.indexOf("`", i + 1);
      if (end > i + 1 && !src.slice(i + 1, end).includes("\n")) {
        flush();
        push(out, { ...style, text: unescape(src.slice(i + 1, end)), code: true });
        i = end + 1;
        continue;
      }
    }
    if (ch === "<") {
      const m = /^<([^<>|]+)(?:\|([^<>]*))?>/.exec(src.slice(i));
      if (m !== null) {
        flush();
        push(out, entity(m[1]!, m[2], style));
        i += m[0].length;
        continue;
      }
    }
    const mark = MARKS[ch];
    if (mark !== undefined && (i === 0 || BOUNDARY.test(src[i - 1]!)) && i + 1 < src.length && !/\s/.test(src[i + 1]!)) {
      let end = -1;
      for (let j = i + 2; j < src.length; j += 1) {
        if (src[j] === "\n") break;
        if (src[j] === ch && !/\s/.test(src[j - 1]!) && (j + 1 === src.length || BOUNDARY.test(src[j + 1]!))) {
          end = j;
          break;
        }
      }
      if (end !== -1) {
        flush();
        parse(src.slice(i + 1, end), { ...style, [mark]: true }, out);
        i = end + 1;
        continue;
      }
    }
    plain += ch;
    i += 1;
  }
  flush();
}

/** Slack mrkdwn → spans: *bold*, _italic_, ~strike~, `code`, ```pre```, <url|label>, <@user>, <#channel>, <!here>. */
export function mrkdwnSpans(text: string): KitSpan[] {
  const out: KitSpan[] = [];
  parse(text, {}, out);
  return out;
}

function textSpans(t: KitText): KitSpan[] {
  if (t.type === "plain_text") return [{ text: t.text }];
  return mrkdwnSpans(t.text);
}

const spansText = (spans: readonly KitSpan[]) => spans.map((s) => (s.userId !== undefined ? `<@${s.userId}>` : s.channelId !== undefined ? `<#${s.channelId}>` : s.text)).join("");
/** An option's or confirm's text as plain text (mrkdwn marks dropped). */
const plainOf = (t: KitText) => (t.type === "plain_text" ? t.text : mrkdwnSpans(t.text).map((s) => s.text).join(""));

function confirmNode(c: KitConfirm | undefined): KitConfirmNode | undefined {
  if (c === undefined) return undefined;
  return { title: c.title.text, text: plainOf(c.text), confirm: c.confirm.text, deny: c.deny.text, style: c.style ?? "default" };
}

function optionNode(o: KitOption): KitOptionNode {
  const n: KitOptionNode = { value: o.value, label: plainOf(o.text) };
  if (o.description !== undefined) n.description = plainOf(o.description);
  return n;
}

function button(b: KitButton): KitButtonNode {
  const n: KitButtonNode = { kind: "button", actionId: b.action_id, label: b.text.text, style: b.style ?? "default" };
  if (b.value !== undefined) n.value = b.value;
  if (b.url !== undefined && isSafeKitUrl(b.url)) n.url = b.url;
  const c = confirmNode(b.confirm);
  if (c !== undefined) n.confirm = c;
  if (b.accessibility_label !== undefined) n.accessibilityLabel = b.accessibility_label;
  return n;
}

/** Sets the optional fields every value element shares. */
function common<N extends KitElementNode>(n: N, e: RawElement): N {
  const m = n as { placeholder?: string; confirm?: KitConfirmNode };
  if ("placeholder" in e && e.placeholder !== undefined) m.placeholder = e.placeholder.text;
  if ("confirm" in e) {
    const c = confirmNode(e.confirm);
    if (c !== undefined) m.confirm = c;
  }
  return n;
}

function elementNode(e: RawElement): KitElementNode {
  const focus = "focus_on_load" in e && e.focus_on_load === true;
  const opts = optionsOf(e).map(optionNode);
  const known = new Set(opts.map((o) => o.value));
  switch (e.type) {
    case "button":
      return button(e);
    case "overflow":
      return common<KitOverflowNode>({ kind: "overflow", actionId: e.action_id, options: opts }, e);
    case "plain_text_input": {
      const n: KitTextInputNode = { kind: "text_input", type: "plain_text_input", actionId: e.action_id, initial: rawInitial(e), multiline: e.multiline === true, focus };
      if (e.placeholder !== undefined) n.placeholder = e.placeholder.text;
      if (e.min_length !== undefined) n.minLength = e.min_length;
      if (e.max_length !== undefined) n.maxLength = e.max_length;
      return n;
    }
    case "datepicker":
      return common<KitDateNode>({ kind: "datepicker", type: "datepicker", actionId: e.action_id, initial: rawInitial(e), focus }, e);
    case "timepicker":
      return common<KitTimeNode>({ kind: "timepicker", type: "timepicker", actionId: e.action_id, initial: rawInitial(e), focus }, e);
    case "checkboxes":
      return common<KitCheckboxesNode>({ kind: "checkboxes", type: "checkboxes", actionId: e.action_id, options: opts, initial: rawInitial(e).filter((v) => known.has(v)), focus }, e);
    case "radio_buttons":
      return common<KitRadioNode>({ kind: "radio_buttons", type: "radio_buttons", actionId: e.action_id, options: opts, initial: rawInitial(e).filter((v) => known.has(v)), focus }, e);
    default: {
      const { source, multi } = SELECT_SOURCE[e.type]!;
      const initial = rawInitial(e);
      const n: KitSelectNode = { kind: "select", type: e.type, actionId: e.action_id, source, multi, options: opts, initial: source === "static" || source === "external" ? initial.filter((v) => known.has(v)) : initial, focus };
      if ("option_groups" in e && e.option_groups !== undefined) n.groups = e.option_groups.map((g) => ({ label: g.label.text, options: g.options.map(optionNode) }));
      if (e.type === "external_select" || e.type === "multi_external_select") n.minQueryLength = e.min_query_length ?? 3;
      if ("max_selected_items" in e && e.max_selected_items !== undefined) n.maxSelected = e.max_selected_items;
      if (e.type === "conversations_select" || e.type === "multi_conversations_select") {
        if (e.filter?.include !== undefined) n.include = [...e.filter.include];
        if (e.filter?.exclude_bot_users === true) n.excludeBots = true;
        if (e.default_to_current_conversation === true) n.defaultToCurrent = true;
      }
      return common(n, e);
    }
  }
}

const RICH_STYLE: (keyof NonNullable<Extract<KitRichInline, { type: "text" }>["style"]>)[] = ["bold", "italic", "strike", "code"];

/** A rich_text inline element → a span (unsafe links become plain text; emoji become their characters). */
function richSpan(e: KitRichInline): KitSpan {
  const style: Style = {};
  if ("style" in e && e.style !== undefined) for (const k of RICH_STYLE) if (e.style[k] === true) style[k] = true;
  switch (e.type) {
    case "text":
      return { ...style, text: e.text };
    case "link":
      return isSafeKitUrl(e.url) || /^mailto:/i.test(e.url) ? { ...style, text: e.text !== undefined && e.text !== "" ? e.text : e.url, link: e.url } : { ...style, text: e.text ?? e.url };
    case "user":
      return { ...style, text: `@${e.user_id}`, userId: e.user_id };
    case "channel":
      return { ...style, text: `#${e.channel_id}`, channelId: e.channel_id };
    case "usergroup":
      return { ...style, text: `@${e.usergroup_id}` };
    case "emoji": {
      const points = (e.unicode ?? "").split("-").map((h) => parseInt(h, 16));
      const ok = e.unicode !== undefined && e.unicode !== "" && points.every((p) => Number.isInteger(p) && p > 0 && p <= 0x10ffff);
      return { text: ok ? String.fromCodePoint(...points) : `:${e.name}:` };
    }
    case "broadcast":
      return { text: `@${e.range}`, broadcast: e.range };
  }
}

function richSpans(list: readonly KitRichInline[]): KitSpan[] {
  const out: KitSpan[] = [];
  for (const e of list) push(out, richSpan(e));
  return out;
}

function richNodes(b: Extract<KitBlock, { type: "rich_text" }>): KitRichNode[] {
  return b.elements.map((e): KitRichNode => {
    switch (e.type) {
      case "rich_text_section":
        return { kind: "paragraph", spans: richSpans(e.elements) };
      case "rich_text_list":
        return { kind: "list", ordered: e.style === "ordered", indent: e.indent ?? 0, offset: e.offset ?? 0, items: e.elements.map((s) => richSpans(s.elements)) };
      case "rich_text_preformatted":
        return { kind: "preformatted", text: e.elements.map((x) => richSpan(x).text).join("") };
      case "rich_text_quote":
        return { kind: "quote", spans: richSpans(e.elements) };
    }
  });
}

const imageNode = (url: string, alt: string): KitImageNode | undefined => (isSafeKitUrl(url) ? { kind: "image", url, alt } : undefined);

/** What a client draws for blocks (a message's or a modal's), in order. Blocks without an id get their index ("0", "1", …). */
export function renderKit(blocks: readonly KitBlock[]): KitNode[] {
  const out: KitNode[] = [];
  blocks.forEach((b, i) => {
    const blockId = b.block_id ?? String(i);
    switch (b.type) {
      case "header":
        out.push({ kind: "header", blockId, text: b.text.text });
        break;
      case "divider":
        out.push({ kind: "divider", blockId });
        break;
      case "section": {
        const node: KitNode = { kind: "section", blockId, fields: (b.fields ?? []).map(textSpans) };
        if (b.text !== undefined) node.text = textSpans(b.text);
        if (b.accessory?.type === "image") {
          const img = imageNode(b.accessory.image_url, b.accessory.alt_text);
          if (img !== undefined) node.accessory = img;
        } else if (b.accessory !== undefined && placementProblem(b.accessory, "accessory") === undefined) node.accessory = elementNode(b.accessory);
        out.push(node);
        break;
      }
      case "context": {
        const elements: KitContextNode[] = [];
        for (const e of b.elements) {
          if (e.type === "image") {
            const img = imageNode(e.image_url, e.alt_text);
            if (img !== undefined) elements.push(img);
          } else elements.push({ kind: "text", spans: textSpans(e) });
        }
        if (elements.length > 0) out.push({ kind: "context", blockId, elements });
        break;
      }
      case "image":
        if (isSafeKitUrl(b.image_url)) {
          const node: KitNode = { kind: "image", blockId, url: b.image_url, alt: b.alt_text };
          if (b.title !== undefined) node.title = b.title.text;
          out.push(node);
        }
        break;
      case "actions": {
        // Elements that can't go here (images, text inputs) are skipped, as are actions blocks left empty.
        const elements = b.elements.flatMap((e) => (e.type !== "image" && placementProblem(e, "actions") === undefined ? [elementNode(e)] : []));
        if (elements.length > 0) out.push({ kind: "actions", blockId, elements });
        break;
      }
      case "input": {
        const e = b.element;
        if (e.type === "image" || placementProblem(e, "input") !== undefined) break;
        const node: KitNode = { kind: "input", blockId, label: b.label.text, optional: b.optional === true, dispatch: b.dispatch_action === true, element: elementNode(e) as KitValueNode };
        if (b.hint !== undefined) node.hint = b.hint.text;
        out.push(node);
        break;
      }
      case "rich_text":
        out.push({ kind: "rich_text", blockId, elements: richNodes(b) });
        break;
    }
  });
  return out;
}

const isValueNode = (n: KitElementNode | KitImageNode): n is KitValueNode => n.kind !== "button" && n.kind !== "overflow" && n.kind !== "image";

/** Every element that holds a value, with its block id, in order. */
export function kitValueElements(nodes: readonly KitNode[]): { blockId: string; element: KitValueNode; input: boolean }[] {
  const out: { blockId: string; element: KitValueNode; input: boolean }[] = [];
  for (const n of nodes) {
    if (n.kind === "section" && n.accessory !== undefined && isValueNode(n.accessory)) out.push({ blockId: n.blockId, element: n.accessory, input: false });
    if (n.kind === "actions") for (const e of n.elements) if (isValueNode(e)) out.push({ blockId: n.blockId, element: e, input: false });
    if (n.kind === "input") out.push({ blockId: n.blockId, element: n.element, input: true });
  }
  return out;
}

/**
 * The values a form starts with: each element's initial value; a conversations select with
 * `default_to_current_conversation` and nothing chosen starts on `channelId` (the conversation it was opened from).
 */
export function initialKitValues(nodes: readonly KitNode[], channelId?: string): KitValues {
  const out: KitValues = {};
  for (const { blockId, element } of kitValueElements(nodes)) {
    let v = element.initial;
    if (v.length === 0 && element.kind === "select" && element.defaultToCurrent === true && channelId !== undefined) v = [channelId];
    (out[blockId] ??= {})[element.actionId] = [...v];
  }
  return out;
}

const valueOf = (values: KitValues, blockId: string, e: KitValueNode) => values[blockId]?.[e.actionId] ?? e.initial;

function optionOut(e: { options: KitOptionNode[] }, value: string, labels?: Record<string, string>): KitOption {
  const label = e.options.find((o) => o.value === value)?.label ?? labels?.[value] ?? value;
  return { text: { type: "plain_text", text: label }, value };
}

/**
 * One element's value in Slack's `state.values` shape (nothing chosen = only `type`, or an empty list for multi ones).
 * `labels` names options the element doesn't list (external selects: option value → the text the app gave it).
 */
export function kitStateValue(e: KitValueNode, v: readonly string[], labels?: Record<string, string>): KitStateValue {
  const first = v[0];
  switch (e.kind) {
    case "text_input":
      return first === undefined || first === "" ? { type: e.type } : { type: e.type, value: first };
    case "datepicker":
      return first === undefined ? { type: e.type } : { type: e.type, selected_date: first };
    case "timepicker":
      return first === undefined ? { type: e.type } : { type: e.type, selected_time: first };
    case "checkboxes":
      return { type: e.type, selected_options: v.map((x) => optionOut(e, x)) };
    case "radio_buttons":
      return first === undefined ? { type: e.type } : { type: e.type, selected_option: optionOut(e, first) };
    case "select": {
      if (e.source === "static" || e.source === "external") return e.multi ? { type: e.type, selected_options: v.map((x) => optionOut(e, x, labels)) } : first === undefined ? { type: e.type } : { type: e.type, selected_option: optionOut(e, first, labels) };
      const out: KitStateValue = { type: e.type };
      if (e.source === "users") {
        if (e.multi) out.selected_users = [...v];
        else if (first !== undefined) out.selected_user = first;
      } else if (e.source === "conversations") {
        if (e.multi) out.selected_conversations = [...v];
        else if (first !== undefined) out.selected_conversation = first;
      } else if (e.multi) out.selected_channels = [...v];
      else if (first !== undefined) out.selected_channel = first;
      return out;
    }
  }
}

/** Slack's `state.values` for every element that holds a value (what the form shows now; `labels`: see KitLabels). */
export function kitState(nodes: readonly KitNode[], values: KitValues, labels?: KitLabels): KitStateValues {
  const out: KitStateValues = {};
  for (const { blockId, element } of kitValueElements(nodes)) {
    (out[blockId] ??= {})[element.actionId] = kitStateValue(element, valueOf(values, blockId, element), labels?.[blockId]?.[element.actionId]);
  }
  return out;
}

/**
 * What stops a modal from being submitted, by block id (empty = fine): a required input left empty (blank text
 * counts), text shorter than `min_length` or longer than `max_length`, more choices than `max_selected_items`.
 */
export function kitInputErrors(nodes: readonly KitNode[], values: KitValues): Record<string, KitInputError> {
  const out: Record<string, KitInputError> = {};
  for (const n of nodes) {
    if (n.kind !== "input") continue;
    const e = n.element;
    const v = valueOf(values, n.blockId, e);
    const empty = e.kind === "text_input" ? (v[0] ?? "").trim() === "" : v.length === 0;
    if (empty) {
      if (!n.optional) out[n.blockId] = "required";
      continue;
    }
    if (e.kind === "text_input") {
      const len = [...(v[0] ?? "")].length;
      if (e.minLength !== undefined && len < e.minLength) out[n.blockId] = "tooShort";
      else if (e.maxLength !== undefined && len > e.maxLength) out[n.blockId] = "tooLong";
    } else if (e.kind === "select" && e.maxSelected !== undefined && v.length > e.maxSelected) out[n.blockId] = "tooMany";
  }
  return out;
}

const ID = /^[A-Za-z0-9_.-]{1,64}$/;
const YMD = /^\d{4}-\d{2}-\d{2}$/;
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** An option an app sent (external selects): plain text, a value of 1–150 characters; else undefined. */
function externalOption(o: KitOption | undefined): KitOption | undefined {
  if (o === undefined || typeof o.value !== "string" || o.value === "" || o.value.length > 150) return undefined;
  if (o.text?.type !== "plain_text" || typeof o.text.text !== "string" || o.text.text === "") return undefined;
  const out: KitOption = { text: { type: "plain_text", text: o.text.text.slice(0, 75) }, value: o.value };
  if (o.description?.type === "plain_text" && o.description.text !== "") out.description = { type: "plain_text", text: o.description.text.slice(0, 75) };
  return out;
}

/** A value a client sent, reduced to the element's real choices; undefined when it doesn't fit the element. */
function sanitizeOne(e: RawElement, sent: KitStateValue): KitStateValue | undefined {
  const opts = optionsOf(e);
  const pick = (v: KitOption | undefined) => (v === undefined ? undefined : opts.find((o) => o.value === v.value));
  const picks = (list: readonly KitOption[] | undefined, max?: number) => {
    const seen = new Set<string>();
    const out: KitOption[] = [];
    for (const v of list ?? []) {
      const o = pick(v);
      if (o !== undefined && !seen.has(o.value)) {
        seen.add(o.value);
        out.push(o);
      }
    }
    return max === undefined ? out : out.slice(0, max);
  };
  const ids = (list: readonly string[] | undefined, max?: number) => {
    const out = [...new Set((list ?? []).filter((x) => ID.test(x)))];
    return max === undefined ? out.slice(0, 100) : out.slice(0, max);
  };
  const id = (x: string | undefined) => (x !== undefined && ID.test(x) ? x : undefined);
  const out: KitStateValue = { type: e.type };
  switch (e.type) {
    case "plain_text_input": {
      if (sent.value !== undefined && sent.value !== "") out.value = sent.value.slice(0, e.max_length ?? 3000);
      return out;
    }
    case "static_select":
    case "radio_buttons": {
      const o = pick(sent.selected_option);
      if (o !== undefined) out.selected_option = o;
      return out;
    }
    case "multi_static_select":
      out.selected_options = picks(sent.selected_options, e.max_selected_items);
      return out;
    case "external_select": {
      // The app supplied the choices as the person typed: any well-formed option of theirs is kept (Slack).
      const o = externalOption(sent.selected_option);
      if (o !== undefined) out.selected_option = o;
      return out;
    }
    case "multi_external_select": {
      const seen = new Set<string>();
      out.selected_options = (sent.selected_options ?? []).flatMap((x) => {
        const o = externalOption(x);
        if (o === undefined || seen.has(o.value)) return [];
        seen.add(o.value);
        return [o];
      }).slice(0, e.max_selected_items ?? 100);
      return out;
    }
    case "checkboxes":
      out.selected_options = picks(sent.selected_options);
      return out;
    case "users_select": {
      const v = id(sent.selected_user);
      if (v !== undefined) out.selected_user = v;
      return out;
    }
    case "multi_users_select":
      out.selected_users = ids(sent.selected_users, e.max_selected_items);
      return out;
    case "conversations_select": {
      const v = id(sent.selected_conversation);
      if (v !== undefined) out.selected_conversation = v;
      return out;
    }
    case "multi_conversations_select":
      out.selected_conversations = ids(sent.selected_conversations, e.max_selected_items);
      return out;
    case "channels_select": {
      const v = id(sent.selected_channel);
      if (v !== undefined) out.selected_channel = v;
      return out;
    }
    case "multi_channels_select":
      out.selected_channels = ids(sent.selected_channels, e.max_selected_items);
      return out;
    case "datepicker":
      if (sent.selected_date !== undefined && YMD.test(sent.selected_date)) out.selected_date = sent.selected_date;
      return out;
    case "timepicker":
      if (sent.selected_time !== undefined && HHMM.test(sent.selected_time)) out.selected_time = sent.selected_time;
      return out;
    default:
      return undefined;
  }
}

/** An element's initial value in `state.values` shape (with the app's own option objects). */
function initialState(e: RawElement): KitStateValue {
  const initial = rawInitial(e);
  const opts = optionsOf(e);
  const asOptions = initial.flatMap((v) => opts.filter((o) => o.value === v));
  const sent: KitStateValue = { type: e.type, selected_options: asOptions, selected_users: initial, selected_conversations: initial, selected_channels: initial };
  const first = initial[0];
  if (first !== undefined) {
    sent.value = first;
    sent.selected_user = first;
    sent.selected_conversation = first;
    sent.selected_channel = first;
    sent.selected_date = first;
    sent.selected_time = first;
  }
  if (asOptions[0] !== undefined) sent.selected_option = asOptions[0];
  return sanitizeOne(e, sent) ?? { type: e.type };
}

/**
 * The `state.values` the API forwards to an app: one entry per element that holds a value — what the client sent,
 * kept only when it fits (real options, the app's own option objects, ids, days "YYYY-MM-DD", times "HH:mm",
 * `max_selected_items`, `max_length`), else the element's initial value. Unknown blocks and actions are dropped.
 */
export function sanitizeKitState(blocks: readonly KitBlock[], values: KitStateValues): KitStateValues {
  const out: KitStateValues = {};
  for (const { blockId, e } of elementsOf(blocks)) {
    if (!isValueElement(e)) continue;
    const sent = values[blockId]?.[e.action_id];
    (out[blockId] ??= {})[e.action_id] = (sent !== undefined ? sanitizeOne(e, sent) : undefined) ?? initialState(e);
  }
  return out;
}

/** One line per block with text (header, section text and fields, context, rich text), for notifications and search. */
export function kitFallbackText(blocks: readonly KitBlock[]): string {
  const lines: string[] = [];
  for (const node of renderKit(blocks)) {
    if (node.kind === "header") lines.push(node.text);
    else if (node.kind === "section") {
      if (node.text !== undefined) lines.push(spansText(node.text));
      for (const f of node.fields) lines.push(spansText(f));
    } else if (node.kind === "context") {
      const t = node.elements.flatMap((e) => (e.kind === "text" ? [spansText(e.spans)] : [])).join(" ");
      if (t !== "") lines.push(t);
    } else if (node.kind === "rich_text") {
      for (const r of node.elements) {
        if (r.kind === "paragraph" || r.kind === "quote") lines.push(spansText(r.spans));
        else if (r.kind === "preformatted") lines.push(r.text);
        else r.items.forEach((item, j) => lines.push(`${r.ordered ? `${r.offset + j + 1}.` : "•"} ${spansText(item)}`));
      }
    }
  }
  return lines.join("\n").trim();
}

// ---------- Select helpers: option groups, external options, server-searched pickers ----------

/** One select element's render node (what `renderKit` draws for it), or undefined for a non-select. */
export function kitSelectNode(e: KitElement): KitSelectNode | undefined {
  if (e.type === "image" || SELECT_SOURCE[e.type] === undefined) return undefined;
  return elementNode(e) as KitSelectNode;
}

/**
 * An app's answer to a `block_suggestion` (Slack: `{ options }` or `{ option_groups }`), checked: plain-text labels,
 * values of 1–150 characters, each value once, at most 100 options (or 100 groups of 100). Anything else is dropped;
 * an unreadable answer is no options.
 */
export function externalOptionsAnswer(raw: unknown): { options: KitOption[]; optionGroups?: KitOptionGroup[] } {
  const obj = typeof raw === "object" && raw !== null ? (raw as Record<string, unknown>) : {};
  const seen = new Set<string>();
  const clean = (list: unknown): KitOption[] =>
    (Array.isArray(list) ? list : []).flatMap((x) => {
      const o = externalOption(x as KitOption);
      if (o === undefined || seen.has(o.value)) return [];
      seen.add(o.value);
      return [o];
    }).slice(0, 100);
  if (Array.isArray(obj.option_groups)) {
    const groups: KitOptionGroup[] = [];
    for (const g of obj.option_groups.slice(0, 100) as unknown[]) {
      const label = (g as { label?: { type?: unknown; text?: unknown } } | null)?.label;
      if (label?.type !== "plain_text" || typeof label.text !== "string" || label.text === "") continue;
      const options = clean((g as { options?: unknown }).options);
      if (options.length > 0) groups.push({ label: { type: "plain_text", text: label.text.slice(0, 75) }, options });
    }
    return { options: groups.flatMap((g) => g.options), optionGroups: groups };
  }
  return { options: clean(obj.options) };
}

/** Where a picker searches: the API query (contracts KitOptionsQuery) for a people / conversation / channel select. */
export type KitPickerQuery = { source: "users" | "conversations" | "channels"; q?: string; include?: string; excludeBots?: "true" };

/**
 * The server search for a select's choices and what the person typed (trimmed); undefined for static and external
 * selects (their choices come from the element or the app). Conversations list every kind (public, private, DMs and
 * group DMs) unless the filter narrows it, like Slack.
 */
export function kitPickerQuery(node: Pick<KitSelectNode, "source" | "include" | "excludeBots">, query: string): KitPickerQuery | undefined {
  if (node.source !== "users" && node.source !== "conversations" && node.source !== "channels") return undefined;
  const out: KitPickerQuery = { source: node.source };
  const q = query.trim();
  if (q !== "") out.q = q.slice(0, 100);
  if (node.source === "conversations" && node.include !== undefined && node.include.length > 0) out.include = [...new Set(node.include)].sort().join(",");
  if (node.excludeBots === true && node.source !== "channels") out.excludeBots = "true";
  return out;
}

/** Whether an external select should ask the app yet (at least `minQueryLength` characters, default 3). */
export function externalQueryReady(node: Pick<KitSelectNode, "minQueryLength">, query: string): boolean {
  return query.trim().length >= (node.minQueryLength ?? 3);
}
